import React from 'react';
import {
  DocPage, PageHeader, Section,
  Para, Callout, Prerequisites, NextStep, Tooltip,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './RateCard.css';

const TOC = [
  { id: 'overview',       label: 'Overview' },
  { id: 'variants',       label: 'Variants & default' },
  { id: 'multi-currency', label: 'Multi-currency' },
];

// ── Combination matrix ────────────────────────────────────────────────────────
// Shows that each pricing dimension combination on a fee gets its own rate card.

function CombinationMatrix({ feeComponent, tagLabels, combinations }) {
  return (
    <div className="rc-cmatrix">
      <div className="rc-cmatrix-header">
        <span className="rc-cmatrix-fc">{feeComponent}</span>
        <span className="rc-cmatrix-sub">one rate card per pricing dimension combination</span>
      </div>
      <table className="rc-cmatrix-table">
        <thead>
          <tr>
            {tagLabels.map((t) => (
              <th key={t} className="rc-cmatrix-th rc-cmatrix-th--tag">{t}</th>
            ))}
            <th className="rc-cmatrix-th rc-cmatrix-th--type">Model</th>
            <th className="rc-cmatrix-th rc-cmatrix-th--value">Rate</th>
          </tr>
        </thead>
        <tbody>
          {combinations.map((combo, i) => (
            <tr key={i} className="rc-cmatrix-tr">
              {combo.tags.map((tag, j) => (
                <td key={j} className="rc-cmatrix-td rc-cmatrix-td--tag">
                  <span className="rc-cmatrix-chip">{tag}</span>
                </td>
              ))}
              <td className="rc-cmatrix-td rc-cmatrix-td--type">{combo.type}</td>
              <td className="rc-cmatrix-td rc-cmatrix-td--value">{combo.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Variant cards ─────────────────────────────────────────────────────────────
// Shows that a single combination can have multiple variants, one as default.

function VariantCards({ feeComponent, tagLabel, tagValue, currency }) {
  return (
    <div className="rc-variants">
      <div className="rc-variants-context">
        <span className="rc-variants-fc">{feeComponent}</span>
        <span className="rc-variants-tag">{tagLabel}: <strong>{tagValue}</strong></span>
      </div>
      <div className="rc-variants-grid">
        <div className="rc-variant-card rc-variant-card--default">
          <div className="rc-variant-card-header">
            <span className="rc-variant-card-id">RC_001</span>
            <span className="rc-variant-card-badge">Default</span>
          </div>
          <div className="rc-variant-card-type">Fixed Price</div>
          <div className="rc-variant-card-price">{currency}3.00</div>
          <p className="rc-variant-card-note">Applied to all customers unless their contract specifies otherwise.</p>
        </div>
        <div className="rc-variant-card">
          <div className="rc-variant-card-header">
            <span className="rc-variant-card-id">RC_002</span>
          </div>
          <div className="rc-variant-card-type">Fixed Price</div>
          <div className="rc-variant-card-price">{currency}2.00</div>
          <p className="rc-variant-card-note">Assigned to high-volume clients on negotiated terms.</p>
        </div>
      </div>
    </div>
  );
}

// ── Multi-currency table ──────────────────────────────────────────────────────
// Same fee + same dimension combination, different currency rate cards.

function MultiCurrencyExample({ feeComponent, tagLabels, tags, rates }) {
  return (
    <div className="rc-cmatrix">
      <div className="rc-cmatrix-header">
        <span className="rc-cmatrix-fc">{feeComponent}</span>
        <span className="rc-cmatrix-sub">
          {tagLabels.map((l, i) => `${l} = ${tags[i]}`).join(', ')} — one rate card per currency
        </span>
      </div>
      <table className="rc-cmatrix-table">
        <thead>
          <tr>
            <th className="rc-cmatrix-th rc-cmatrix-th--tag">Currency</th>
            <th className="rc-cmatrix-th rc-cmatrix-th--type">Model</th>
            <th className="rc-cmatrix-th rc-cmatrix-th--value">Rate</th>
            <th className="rc-cmatrix-th rc-cmatrix-th--type">Applied to</th>
          </tr>
        </thead>
        <tbody>
          {rates.map((r, i) => (
            <tr key={i} className="rc-cmatrix-tr">
              <td className="rc-cmatrix-td rc-cmatrix-td--tag">
                <span className="rc-cmatrix-chip">{r.currency}</span>
              </td>
              <td className="rc-cmatrix-td rc-cmatrix-td--type">{r.type}</td>
              <td className="rc-cmatrix-td rc-cmatrix-td--value">{r.value}</td>
              <td className="rc-cmatrix-td rc-cmatrix-td--type">{r.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function RateCard({ onNavigate }) {
  const { industry } = useIndustry();
  const { rateCardMatrix: matrix, rateCard: rc, multiCurrency: mc } = industry;
  const currency = rc.currency;
  const description = (
    <>
      A Rate Card is the pricing rule for a specific fee and{' '}
      <Tooltip tip="Attributes defined on the product along which price can vary. A fee opts in to the ones that affect its price. Each unique combination gets its own rate card.">
        pricing dimension
      </Tooltip>
      {' '}combination — it defines the model and rates the billing engine uses to produce a charge.
    </>
  );

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Pricing"
        title="Rate Card"
        description={description}
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'fee', label: 'Fees' },
          { id: 'pricing-dimensions', label: 'Pricing Dimensions' },
        ]}
      />

      <Section id="overview" title="Overview">
        <Para>
          A fee is priced through rate cards, but not with a single rate card for the
          whole component. Because a product defines pricing dimensions — {matrix.tagLabels.join(', ')} —
          each unique combination of those attribute values creates its own rate card slot. A{' '}
          {matrix.tagLabels[0]} = {matrix.combinations[0].tags[0]},{' '}
          {matrix.tagLabels[1]} = {matrix.combinations[0].tags[1]} customer can be priced
          completely differently from a{' '}
          {matrix.tagLabels[0]} = {matrix.combinations[3].tags[0]},{' '}
          {matrix.tagLabels[1]} = {matrix.combinations[3].tags[1]} customer — with a different
          pricing model, different rates, or both.
        </Para>
        <CombinationMatrix
          feeComponent={matrix.feeComponent}
          tagLabels={matrix.tagLabels}
          combinations={matrix.combinations}
        />
        <Callout type="info">
          Each row above is an independent rate card — different pricing model, different rates,
          or both. A fee with no pricing dimensions has exactly one slot. Each dimension value
          added multiplies the slot count.
        </Callout>
        <Para>
          Rate cards are defined at the <strong>company level</strong>. Five pricing models are
          available — Fixed Price, Rate Per Unit, Graduated, Volume Based, and Min/Max. See{' '}
          <strong>Pricing Models</strong> for a full reference, and <strong>Calculator</strong>{' '}
          to explore how each model computes a charge. When a pricing dimension has{' '}
          <strong>dimension groups</strong> defined, slots are scoped to the group rather than
          the individual value — usage for grouped values is pooled before the pricing model
          runs. See <strong>Pricing Dimensions → Dimension groups</strong>.
        </Para>
      </Section>

      <Section id="variants" title="Variants & default">
        <Para>
          Within a single combination slot, you can define multiple rate card{' '}
          <strong>variants</strong> — different pricing rules for the same combination.
          Exactly one variant must be marked as the <strong>default</strong>. The default
          is applied to all customers for that combination unless their contract explicitly
          selects a different variant.
        </Para>
        <Para>
          This lets you maintain a standard rate alongside negotiated or promotional rates,
          without duplicating the combination setup.
        </Para>
        <VariantCards
          feeComponent={matrix.feeComponent}
          tagLabel={matrix.tagLabels[0]}
          tagValue={matrix.combinations[0].tags[0]}
          currency={currency}
        />
        <Callout type="info" title="How the default is resolved">
          When billing runs, the system looks up the rate card variant assigned to the
          customer's contract for that fee and dimension combination. If none is assigned,
          it falls back to the default. Only customers on non-standard pricing need an
          explicit assignment — everyone else is covered automatically.
        </Callout>
        <Callout type="tip" title="Changing the default">
          Promoting a variant to default does not retroactively affect already-billed periods.
          It only changes which variant new and unbilled usage resolves to.
        </Callout>
      </Section>

      <Section id="multi-currency" title="Multi-currency">
        <Para>
          Because rate cards are defined at the company level, you can create multiple rate cards
          for the <strong>same fee and dimension combination</strong> — one per currency. When
          billing runs for a contract, the system selects the rate card whose currency matches the
          contract's <strong>base currency</strong>. No manual selection is needed; the customer's
          billing currency drives the lookup automatically.
        </Para>
        <Para>
          This means a single combination slot — say, {mc.feeComponent} /{' '}
          {mc.tagLabels.map((l, i) => `${l} = ${mc.tags[i]}`).join(', ')} — can hold
          separate rate cards for {mc.rates.map((r) => r.currency).join(', ')}, each with its own
          rate independently configured.
        </Para>
        <MultiCurrencyExample
          feeComponent={mc.feeComponent}
          tagLabels={mc.tagLabels}
          tags={mc.tags}
          rates={mc.rates}
        />
        <Callout type="info" title="Currency is a third scoping dimension">
          A rate card slot is uniquely identified by three things: fee, dimension combination,
          and currency. Two customers with the same dimension values but different billing currencies
          will each resolve to their own rate card — with fully independent pricing rules and rates.
        </Callout>
        <Callout type="tip" title="Missing currency fallback">
          If no rate card exists for a customer's base currency on a given combination, billing
          will error rather than silently fall back to another currency. Ensure every supported
          currency has a rate card defined for all active combinations.
        </Callout>
      </Section>

      <NextStep
        onNavigate={onNavigate}
        items={[
          { id: 'pricing-models',      title: 'Pricing Models',   desc: 'Five models — Fixed, Per Unit, Graduated, Volume, Min/Max — one applies per rate card.' },
          { id: 'rate-card-calculator', title: 'Calculator',       desc: 'Step through a charge calculation interactively with any model.' },
        ]}
      />
    </DocPage>
  );
}
